// web/src/routes/Admin.tsx
// Operator-only dashboard. The backend gates by ADMIN_PARENT_IDS — the
// SPA simply renders whatever the API returns. If /admin/metrics 403s,
// we redirect to /dashboard so non-admins never see a half-rendered shell.

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { useAdminApi } from './api-admin';
import type {
  AdminMetrics,
  AdminParentRow,
  AdminParentDetail,
  FailedPaymentRow,
  DormancyRow,
} from './api-admin';

type Tab = 'parents' | 'failed' | 'dormancy';

const css = `
  .admin { padding: 24px; max-width: 1280px; margin: 0 auto; font-family: var(--font-body); color: var(--ink); }
  .admin h1 { font-family: var(--font-display); font-weight: 700; font-size: 1.8rem; margin: 0 0 4px; letter-spacing: -0.01em; }
  .admin .sub { color: var(--muted); font-size: 13px; margin-bottom: 24px; }
  .admin .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; margin-bottom: 28px; }
  .admin .kpi { background: white; border: 1px solid var(--border, #e5e0d7); border-radius: 10px; padding: 14px 16px; }
  .admin .kpi .label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--muted); margin-bottom: 4px; }
  .admin .kpi .value { font-family: var(--font-display); font-size: 1.6rem; font-weight: 700; line-height: 1; }
  .admin .kpi .hint { font-size: 11px; color: var(--muted); margin-top: 4px; }
  .admin .kpi.alert { border-color: #c75a3d; }
  .admin .kpi.alert .value { color: #c75a3d; }

  .admin .tabs { display: flex; gap: 0; border-bottom: 1px solid var(--border, #e5e0d7); margin-bottom: 14px; }
  .admin .tab { background: none; border: none; padding: 10px 16px; font-family: var(--font-body); font-size: 14px; cursor: pointer; color: var(--muted); border-bottom: 2px solid transparent; }
  .admin .tab.active { color: var(--ink); border-bottom-color: var(--green); font-weight: 600; }
  .admin .tab .count { background: var(--bg, #faf5ec); border-radius: 10px; padding: 1px 7px; margin-left: 6px; font-size: 11px; font-weight: 600; }

  .admin .toolbar { display: flex; gap: 8px; align-items: center; margin-bottom: 12px; flex-wrap: wrap; }
  .admin .toolbar input, .admin .toolbar select { padding: 8px 10px; border: 1px solid var(--border, #e5e0d7); border-radius: 6px; font-size: 13px; background: white; }
  .admin .toolbar input { flex: 1; min-width: 200px; max-width: 340px; }
  .admin .toolbar .meta { margin-left: auto; color: var(--muted); font-size: 12px; }

  .admin table { width: 100%; border-collapse: collapse; background: white; border: 1px solid var(--border, #e5e0d7); border-radius: 8px; overflow: hidden; font-size: 13px; }
  .admin th { text-align: left; padding: 10px 12px; background: #fafaf7; font-weight: 600; color: var(--muted); font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; border-bottom: 1px solid var(--border, #e5e0d7); }
  .admin td { padding: 10px 12px; border-bottom: 1px solid #f0ece4; vertical-align: middle; }
  .admin tr:last-child td { border-bottom: none; }
  .admin tr.row:hover { background: #fafaf7; cursor: pointer; }
  .admin tr.row.suspended { background: #fdf4f0; }
  .admin .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
  .admin .badge.free { background: #f0ece4; color: var(--muted); }
  .admin .badge.stats { background: #fef3c7; color: #92400e; }
  .admin .badge.reports { background: #d1fae5; color: #065f46; }
  .admin .badge.suspended { background: #fee2e2; color: #991b1b; }
  .admin .badge.archived { background: #e5e7eb; color: #374151; }
  .admin .badge.failed { background: #fee2e2; color: #991b1b; }
  .admin .empty { padding: 32px; text-align: center; color: var(--muted); background: white; border: 1px dashed var(--border, #e5e0d7); border-radius: 8px; }

  .admin .drawer-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 100; }
  .admin .drawer { position: fixed; top: 0; right: 0; bottom: 0; width: 480px; max-width: 90vw; background: white; box-shadow: -4px 0 24px rgba(0,0,0,0.15); z-index: 101; overflow-y: auto; }
  .admin .drawer header { padding: 18px 24px; border-bottom: 1px solid var(--border, #e5e0d7); display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; background: white; z-index: 1; }
  .admin .drawer header h2 { margin: 0; font-family: var(--font-display); font-size: 1.1rem; font-weight: 600; word-break: break-all; }
  .admin .drawer header button.close { background: none; border: none; font-size: 24px; cursor: pointer; color: var(--muted); line-height: 1; padding: 0; }
  .admin .drawer .body { padding: 18px 24px; }
  .admin .drawer dl { margin: 0; }
  .admin .drawer dt { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--muted); margin-top: 14px; }
  .admin .drawer dd { margin: 2px 0 0; font-size: 13px; word-break: break-all; }
  .admin .drawer .actions { display: flex; gap: 8px; margin-top: 24px; padding-top: 18px; border-top: 1px solid var(--border, #e5e0d7); }
  .admin .drawer button.action { padding: 10px 14px; border-radius: 6px; border: 1px solid var(--border, #e5e0d7); background: white; cursor: pointer; font-size: 13px; font-weight: 500; }
  .admin .drawer button.action.danger { background: #c75a3d; color: white; border-color: #c75a3d; }
  .admin .drawer button.action.confirm { background: var(--green); color: white; border-color: var(--green); }
  .admin .drawer textarea { width: 100%; padding: 8px; border: 1px solid var(--border, #e5e0d7); border-radius: 6px; font-family: var(--font-body); font-size: 13px; resize: vertical; min-height: 60px; }

  .admin .toast { position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); background: var(--ink); color: var(--bg, #faf5ec); padding: 10px 18px; border-radius: 6px; font-size: 13px; z-index: 200; }
  .admin .toast.err { background: #c75a3d; }
`;

function fmt(d?: string): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtBytes(b?: number): string {
  if (!b || b === 0) return '0';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function planBadge(p: string) {
  const cls = p === 'reports' ? 'reports' : p === 'stats' ? 'stats' : 'free';
  return <span className={`badge ${cls}`}>{p}</span>;
}

export function Admin() {
  const { signOut, user } = useAuth();
  const api = useAdminApi();

  const [bootChecked, setBootChecked] = useState(false);
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [tab, setTab] = useState<Tab>('parents');

  const [parents, setParents] = useState<AdminParentRow[]>([]);
  const [failed, setFailed] = useState<FailedPaymentRow[]>([]);
  const [dormancy, setDormancy] = useState<DormancyRow[]>([]);
  const [loadingList, setLoadingList] = useState(false);

  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminParentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showSuspendForm, setShowSuspendForm] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [acting, setActing] = useState(false);

  const [toast, setToast] = useState<{ text: string; err?: boolean } | null>(null);

  // Boot: hit /metrics; 403 → kick to /dashboard, anything else → render.
  useEffect(() => {
    api.metrics()
      .then((m) => { setMetrics(m); setBootChecked(true); })
      .catch((err) => {
        if (err?.status === 403) {
          setToast({ text: 'Not an admin — signing out.', err: true });
          setTimeout(() => signOut(), 1500);
        } else {
          setBootChecked(true);
          setToast({ text: `Failed to load metrics: ${err.message ?? err}`, err: true });
        }
      });
  }, [api, signOut]);

  // Tab load.
  useEffect(() => {
    if (!bootChecked) return;
    setLoadingList(true);
    if (tab === 'parents') {
      api.listParents({
        plan: planFilter || undefined,
        status: statusFilter || undefined,
        search: search || undefined,
      })
        .then((r) => setParents(r.items))
        .catch((e) => setToast({ text: `Parents: ${e.message}`, err: true }))
        .finally(() => setLoadingList(false));
    } else if (tab === 'failed') {
      api.failedPayments()
        .then((r) => setFailed(r.items))
        .catch((e) => setToast({ text: `Failed payments: ${e.message}`, err: true }))
        .finally(() => setLoadingList(false));
    } else if (tab === 'dormancy') {
      api.dormancy()
        .then((r) => setDormancy(r.items))
        .catch((e) => setToast({ text: `Dormancy: ${e.message}`, err: true }))
        .finally(() => setLoadingList(false));
    }
  }, [tab, bootChecked, planFilter, statusFilter, search, api]);

  // Detail load.
  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    setDetailLoading(true);
    api.getParent(selected)
      .then(setDetail)
      .catch((e) => setToast({ text: `Detail: ${e.message}`, err: true }))
      .finally(() => setDetailLoading(false));
  }, [selected, api]);

  // Toast auto-dismiss.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const reloadAfterAction = async () => {
    const [m, ps] = await Promise.all([
      api.metrics(),
      api.listParents({
        plan: planFilter || undefined,
        status: statusFilter || undefined,
        search: search || undefined,
      }),
    ]);
    setMetrics(m);
    setParents(ps.items);
    if (selected) api.getParent(selected).then(setDetail);
  };

  const onSuspend = async () => {
    if (!detail || !suspendReason.trim()) return;
    setActing(true);
    try {
      const r = await api.suspend(detail.parentId, suspendReason.trim());
      setToast({ text: `Suspended ${detail.email}. Stripe: ${r.stripe}` });
      setShowSuspendForm(false);
      setSuspendReason('');
      await reloadAfterAction();
    } catch (e) {
      setToast({ text: `Suspend failed: ${(e as Error).message}`, err: true });
    } finally { setActing(false); }
  };

  const onUnsuspend = async () => {
    if (!detail) return;
    setActing(true);
    try {
      const r = await api.unsuspend(detail.parentId);
      setToast({ text: `Unsuspended ${detail.email}. Stripe: ${r.stripe}` });
      await reloadAfterAction();
    } catch (e) {
      setToast({ text: `Unsuspend failed: ${(e as Error).message}`, err: true });
    } finally { setActing(false); }
  };

  const tabCounts = useMemo(() => ({
    parents: metrics?.totalParents ?? 0,
    failed: metrics?.paymentFailedCount ?? 0,
    dormancy: metrics?.dormantCount ?? 0,
  }), [metrics]);

  if (!bootChecked) {
    return <div className="admin"><style>{css}</style><p style={{ color: 'var(--muted)' }}>Loading admin…</p></div>;
  }

  return (
    <div className="admin">
      <style>{css}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>Super · MyFootballJournal</h1>
          <div className="sub">
            {metrics?.totalParents ?? 0} parents · MRR {metrics?.mrrFormatted ?? '—'}
            {metrics?.truncated ? ' · WARN: 5k scan cap hit' : ''}
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>{user?.email}</span>
          <button onClick={signOut} style={{ background: 'none', border: '1px solid #d4cfc4', borderRadius: 4, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>Sign out</button>
        </div>
      </div>

      <div className="kpis">
        <Kpi label="MRR (approx)" value={metrics?.mrrFormatted ?? '—'} hint="excludes trials & coupons" />
        <Kpi label="Paid parents" value={String(metrics?.paidParents ?? 0)} hint={`${metrics?.statsParents ?? 0} Stats · ${metrics?.reportsParents ?? 0} Reports`} />
        <Kpi label="Free" value={String(metrics?.freeParents ?? 0)} />
        <Kpi label="Total" value={String(metrics?.totalParents ?? 0)} />
        <Kpi label="Signups today" value={String(metrics?.signupsToday ?? 0)} hint={`${metrics?.signupsThisWeek ?? 0} this week`} />
        <Kpi label="Signups (30d)" value={String(metrics?.signupsThisMonth ?? 0)} />
        <Kpi label="Suspended" value={String(metrics?.suspendedCount ?? 0)} alert={!!metrics?.suspendedCount} />
        <Kpi label="Failed payments" value={String(metrics?.paymentFailedCount ?? 0)} alert={!!metrics?.paymentFailedCount} />
        <Kpi label="Dormant" value={String(metrics?.dormantCount ?? 0)} hint="any warning sent" />
      </div>

      <div className="tabs">
        <button className={`tab ${tab === 'parents' ? 'active' : ''}`} onClick={() => setTab('parents')}>
          Parents <span className="count">{tabCounts.parents}</span>
        </button>
        <button className={`tab ${tab === 'failed' ? 'active' : ''}`} onClick={() => setTab('failed')}>
          Failed payments <span className="count">{tabCounts.failed}</span>
        </button>
        <button className={`tab ${tab === 'dormancy' ? 'active' : ''}`} onClick={() => setTab('dormancy')}>
          Dormancy <span className="count">{tabCounts.dormancy}</span>
        </button>
      </div>

      {tab === 'parents' && (
        <>
          <div className="toolbar">
            <input
              placeholder="Search email or parentId…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
              <option value="">All plans</option>
              <option value="free">Free</option>
              <option value="stats">Stats</option>
              <option value="reports">Reports</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="archived">Archived</option>
            </select>
            <span className="meta">{parents.length} shown · {loadingList ? 'loading…' : 'live'}</span>
          </div>
          {parents.length === 0 ? (
            <div className="empty">{loadingList ? 'Loading…' : 'No parents match these filters.'}</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Kids</th>
                  <th>Games</th>
                  <th>Images</th>
                  <th>Voice</th>
                  <th>Storage</th>
                  <th>Sent</th>
                  <th>Signed up</th>
                  <th>Last login</th>
                </tr>
              </thead>
              <tbody>
                {parents.map((p) => (
                  <tr key={p.parentId}
                      className={`row ${p.status === 'suspended' ? 'suspended' : ''}`}
                      onClick={() => setSelected(p.parentId)}>
                    <td>{p.email}{p.displayName ? <div style={{ color: 'var(--muted)', fontSize: 11 }}>{p.displayName}</div> : null}</td>
                    <td>{planBadge(p.plan)}</td>
                    <td>
                      {p.status === 'suspended' && <span className="badge suspended">SUS</span>}
                      {p.archived && <span className="badge archived">ARC</span>}
                      {p.paymentFailedAt && <span className="badge failed">£✗</span>}
                      {p.status === 'active' && !p.archived && !p.paymentFailedAt && <span style={{ color: 'var(--muted)' }}>—</span>}
                    </td>
                    <td>{p.kidsCount}</td>
                    <td>{p.matchesTotal ?? 0}</td>
                    <td>{p.photoCount ?? 0}</td>
                    <td>{p.voiceNoteCount ?? 0}</td>
                    <td>{fmtBytes(p.storageBytes)}</td>
                    <td>{p.matchSendCount}</td>
                    <td>{fmt(p.createdAt)}</td>
                    <td>{fmt(p.lastLoginAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {tab === 'failed' && (
        failed.length === 0 ? (
          <div className="empty">{loadingList ? 'Loading…' : 'No failed payments. Nice.'}</div>
        ) : (
          <table>
            <thead><tr><th>Email</th><th>Plan</th><th>Failed at</th><th>Reason</th><th>Stripe</th></tr></thead>
            <tbody>
              {failed.map((f) => (
                <tr key={f.parentId} className="row" onClick={() => setSelected(f.parentId)}>
                  <td>{f.email}</td>
                  <td>{planBadge(f.plan)}</td>
                  <td>{fmt(f.paymentFailedAt)}</td>
                  <td>{f.paymentFailedReason ?? '—'}</td>
                  <td>{f.stripeCustomerId ? (
                    <a href={`https://dashboard.stripe.com/customers/${f.stripeCustomerId}`} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>open</a>
                  ) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {tab === 'dormancy' && (
        dormancy.length === 0 ? (
          <div className="empty">{loadingList ? 'Loading…' : 'No dormant accounts.'}</div>
        ) : (
          <table>
            <thead><tr><th>Email</th><th>Plan</th><th>Stage</th><th>W1</th><th>W2</th><th>W3</th><th>Last login</th><th>Archived</th></tr></thead>
            <tbody>
              {dormancy.map((d) => (
                <tr key={d.parentId} className="row" onClick={() => setSelected(d.parentId)}>
                  <td>{d.email}</td>
                  <td>{planBadge(d.plan)}</td>
                  <td><span className="badge failed">{d.stage}</span></td>
                  <td>{fmt(d.dormancyWarn1At)}</td>
                  <td>{fmt(d.dormancyWarn2At)}</td>
                  <td>{fmt(d.dormancyWarn3At)}</td>
                  <td>{fmt(d.lastLoginAt)}</td>
                  <td>{d.archived ? 'yes' : 'no'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {selected && (
        <>
          <div className="drawer-overlay" onClick={() => { setSelected(null); setShowSuspendForm(false); }} />
          <aside className="drawer">
            <header>
              <h2>{detail?.email ?? selected}</h2>
              <button className="close" onClick={() => { setSelected(null); setShowSuspendForm(false); }} aria-label="Close">×</button>
            </header>
            <div className="body">
              {detailLoading ? <p>Loading…</p> : !detail ? <p>Not found.</p> : (
                <>
                  <dl>
                    <dt>Parent ID</dt><dd>{detail.parentId}</dd>
                    <dt>Plan</dt><dd>{planBadge(detail.plan)}</dd>
                    <dt>Status</dt><dd>
                      {detail.status === 'suspended' ? (
                        <>
                          <span className="badge suspended">suspended</span>
                          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                            since {fmt(detail.suspendedAt)} — {detail.suspendedReason ?? 'no reason given'}
                          </div>
                        </>
                      ) : <span className="badge stats" style={{ background: '#d1fae5', color: '#065f46' }}>active</span>}
                      {detail.archived && <span className="badge archived" style={{ marginLeft: 6 }}>archived</span>}
                    </dd>
                    <dt>Kids</dt><dd>{detail.kidsCount ?? 0}</dd>
                    <dt>Matches by season</dt><dd>{JSON.stringify(detail.matchesUsedByYear ?? {})} · total {detail.matchesTotal ?? 0}</dd>

                    {detail.storage && (
                      <>
                        <dt>Storage (S3)</dt>
                        <dd>
                          <strong>{fmtBytes(detail.storage.totalBytes)}</strong> across {detail.storage.totalObjects} objects
                          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 4 }}>
                            {detail.storage.matchPhotos} match photos · {detail.storage.voiceNotes} voice notes · {detail.storage.kidProfilePhotos} kid profile photos
                          </div>
                        </dd>
                      </>
                    )}

                    {detail.kids && detail.kids.length > 0 && (
                      <>
                        <dt>Per-kid breakdown</dt>
                        <dd>
                          <table style={{ width: '100%', fontSize: 12, marginTop: 4, borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ color: 'var(--muted)', textAlign: 'left' }}>
                                <th style={{ padding: '4px 6px', fontWeight: 600 }}>Name</th>
                                <th style={{ padding: '4px 6px', fontWeight: 600 }}>Photos</th>
                                <th style={{ padding: '4px 6px', fontWeight: 600 }}>Voice</th>
                                <th style={{ padding: '4px 6px', fontWeight: 600 }}>Storage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detail.kids.map((k) => (
                                <tr key={k.kidId} style={{ borderTop: '1px solid #f0ece4' }}>
                                  <td style={{ padding: '4px 6px' }}>
                                    {k.name ?? 'unnamed'} {k.surname ?? ''}
                                    {k.deletedAt && <span style={{ color: 'var(--muted)', marginLeft: 4 }}>(deleted)</span>}
                                  </td>
                                  <td style={{ padding: '4px 6px' }}>{k.photos}</td>
                                  <td style={{ padding: '4px 6px' }}>{k.voiceNotes}</td>
                                  <td style={{ padding: '4px 6px' }}>{fmtBytes(k.bytes)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </dd>
                      </>
                    )}
                    <dt>Send-to-family (lifetime)</dt><dd>{detail.sendToFamilyTotalCount ?? 0}</dd>
                    <dt>Send-to-family today</dt><dd>{detail.matchSendCount ?? 0} ({detail.matchSendCountDay ?? '—'})</dd>
                    <dt>Malware uploads</dt><dd>{detail.malwareUploadCount ?? 0}{detail.lastMalwareUploadAt ? ` · last ${fmt(detail.lastMalwareUploadAt)}` : ''}</dd>
                    <dt>Signed up</dt><dd>{fmt(detail.createdAt)}</dd>
                    <dt>Last login</dt><dd>{fmt(detail.lastLoginAt)}</dd>

                    {detail.stripeCustomerId && (
                      <>
                        <dt>Stripe customer</dt>
                        <dd>
                          <a href={`https://dashboard.stripe.com/customers/${detail.stripeCustomerId}`} target="_blank" rel="noopener">{detail.stripeCustomerId}</a>
                        </dd>
                      </>
                    )}
                    {detail.stripeSubscriptionId && (
                      <>
                        <dt>Stripe subscription</dt>
                        <dd>
                          <a href={`https://dashboard.stripe.com/subscriptions/${detail.stripeSubscriptionId}`} target="_blank" rel="noopener">{detail.stripeSubscriptionId}</a>
                        </dd>
                      </>
                    )}
                    {detail.paymentFailedAt && (
                      <>
                        <dt>Payment failed</dt>
                        <dd>{fmt(detail.paymentFailedAt)} — {detail.paymentFailedReason ?? 'no reason'}</dd>
                      </>
                    )}
                    {(detail.dormancyWarn1At || detail.dormancyWarn2At || detail.dormancyWarn3At) && (
                      <>
                        <dt>Dormancy warnings</dt>
                        <dd>
                          W1 {fmt(detail.dormancyWarn1At)}<br />
                          W2 {fmt(detail.dormancyWarn2At)}<br />
                          W3 {fmt(detail.dormancyWarn3At)}
                        </dd>
                      </>
                    )}
                    {detail.suspensionHistory && detail.suspensionHistory.length > 0 && (
                      <>
                        <dt>Suspension history</dt>
                        <dd>
                          {detail.suspensionHistory.slice().reverse().map((h, i) => (
                            <div key={i} style={{ marginBottom: 6, fontSize: 12 }}>
                              <strong>{h.action}</strong> · {fmt(h.at)} · by {h.byEmail ?? h.by.slice(0, 8)}<br />
                              <span style={{ color: 'var(--muted)' }}>{h.reason}</span>
                            </div>
                          ))}
                        </dd>
                      </>
                    )}
                  </dl>

                  <div className="actions">
                    {detail.status === 'suspended' ? (
                      <button className="action confirm" onClick={onUnsuspend} disabled={acting}>
                        {acting ? 'Working…' : 'Unsuspend'}
                      </button>
                    ) : showSuspendForm ? (
                      <div style={{ flex: 1 }}>
                        <textarea
                          placeholder="Reason for suspension (required, kept in audit log)…"
                          value={suspendReason}
                          onChange={(e) => setSuspendReason(e.target.value)}
                        />
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button className="action danger" onClick={onSuspend} disabled={acting || !suspendReason.trim()}>
                            {acting ? 'Suspending…' : 'Confirm suspend'}
                          </button>
                          <button className="action" onClick={() => { setShowSuspendForm(false); setSuspendReason(''); }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button className="action danger" onClick={() => setShowSuspendForm(true)}>Suspend</button>
                    )}
                  </div>
                </>
              )}
            </div>
          </aside>
        </>
      )}

      {toast && <div className={`toast ${toast.err ? 'err' : ''}`}>{toast.text}</div>}
    </div>
  );
}

function Kpi({ label, value, hint, alert }: { label: string; value: string; hint?: string; alert?: boolean }) {
  return (
    <div className={`kpi ${alert ? 'alert' : ''}`}>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}
